import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a flipclock-style countdown timer.
 * Resets every Monday at 24:00 (Tuesday 0:00).
 * @extends {Component}
 */
class IbdCountdown extends Component {
  /** @type {number|null} */
  #countdownInterval = null;

  /** @type {string} */
  #endWeekday = 'Monday';

  /** @type {string} */
  #endTime = '24:00';

  /** @type {string} */
  #timezone = 'auto';

  /** @type {string|null} */
  #detectedTimezone = null;

  /** @type {HTMLElement|null} */
  #announcementElement = null;

  /** @type {number} */
  #lastAnnouncedHours = -1;

  /** @type {HTMLElement[]} */
  #daysElements = [];

  /** @type {HTMLElement[]} */
  #hoursElements = [];

  /** @type {HTMLElement[]} */
  #minutesElements = [];

  /** @type {HTMLElement[]} */
  #secondsElements = [];

  constructor() {
    super();
  }

  /**
   * Detects the user's timezone using multiple methods for better accuracy.
   * @returns {Promise<string|null>} Detected timezone or null if detection fails
   */
  async #detectUserTimezone() {
    try {
      // Method 1: Try to get timezone from browser's Intl API (most accurate)
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTimezone && browserTimezone !== 'UTC') {
        return browserTimezone;
      }

      // Method 2: Use Shopify's browsing context suggestions as fallback
      if (typeof Theme !== 'undefined' && Theme.routes && 'root_url' in Theme.routes) {
        try {
          const response = await fetch(
            Theme.routes.root_url +
              'browsing_context_suggestions.json' +
              '?country[enabled]=true' +
              '&language[enabled]=true'
          );

          if (!response.ok) {
            throw new Error('Failed to fetch browsing context suggestions');
          }

          const data = await response.json();
          if (!data.suggestions || data.suggestions.length === 0) {
            throw new Error('No browsing context suggestions found');
          }

          const suggestion = data.suggestions[0];
          const country = suggestion.parts?.country?.handle;
          if (!country) {
            throw new Error('No country found in browsing context suggestions');
          }

          // For countries with multiple timezones, use more specific defaults
          const timezoneMap = {
            US: 'America/New_York', // Default to Eastern Time for US
            CA: 'America/Toronto', // Default to Eastern Time for Canada
            GB: 'Europe/London',
            AU: 'Australia/Sydney',
            DE: 'Europe/Berlin',
            FR: 'Europe/Paris',
            JP: 'Asia/Tokyo',
            CN: 'Asia/Shanghai',
            IN: 'Asia/Kolkata',
            BR: 'America/Sao_Paulo',
            MX: 'America/Mexico_City',
            IT: 'Europe/Rome',
            ES: 'Europe/Madrid',
            NL: 'Europe/Amsterdam',
            SE: 'Europe/Stockholm',
            NO: 'Europe/Oslo',
            DK: 'Europe/Copenhagen',
            FI: 'Europe/Helsinki',
            PL: 'Europe/Warsaw',
            RU: 'Europe/Moscow',
            TR: 'Europe/Istanbul',
            ZA: 'Africa/Johannesburg',
            EG: 'Africa/Cairo',
            NG: 'Africa/Lagos',
            KE: 'Africa/Nairobi',
            AR: 'America/Argentina/Buenos_Aires',
            CL: 'America/Santiago',
            CO: 'America/Bogota',
            PE: 'America/Lima',
            VE: 'America/Caracas',
            KR: 'Asia/Seoul',
            TH: 'Asia/Bangkok',
            SG: 'Asia/Singapore',
            MY: 'Asia/Kuala_Lumpur',
            ID: 'Asia/Jakarta',
            PH: 'Asia/Manila',
            VN: 'Asia/Ho_Chi_Minh',
            NZ: 'Pacific/Auckland',
          };

          const detectedTimezone = timezoneMap[/** @type {keyof typeof timezoneMap} */ (country)];
          if (!detectedTimezone) {
            throw new Error('No timezone found in timezone map');
          }

          return detectedTimezone;
        } catch (error) {
          console.warn('IBD | Countdown | Error fetching browsing context suggestions:', error);
        }
      }

      // Method 3: Try to detect from timezone offset (less accurate)
      const offset = new Date().getTimezoneOffset();
      const offsetHours = -offset / 60;

      // Map common timezone offsets to timezones
      const offsetMap = {
        '-12': 'Pacific/Kwajalein',
        '-11': 'Pacific/Midway',
        '-10': 'Pacific/Honolulu',
        '-9': 'America/Anchorage',
        '-8': 'America/Los_Angeles',
        '-7': 'America/Denver',
        '-6': 'America/Chicago',
        '-5': 'America/New_York',
        '-4': 'America/Caracas',
        '-3': 'America/Sao_Paulo',
        '-2': 'Atlantic/South_Georgia',
        '-1': 'Atlantic/Azores',
        '0': 'Europe/London',
        '1': 'Europe/Paris',
        '2': 'Europe/Athens',
        '3': 'Europe/Moscow',
        '4': 'Asia/Dubai',
        '5': 'Asia/Karachi',
        '6': 'Asia/Dhaka',
        '7': 'Asia/Bangkok',
        '8': 'Asia/Shanghai',
        '9': 'Asia/Tokyo',
        '10': 'Australia/Sydney',
        '11': 'Pacific/Norfolk',
        '12': 'Pacific/Fiji',
      };

      const offsetKey = offsetHours.toString();
      const offsetTimezone = offsetMap[/** @type {keyof typeof offsetMap} */ (offsetKey)];
      if (offsetTimezone) {
        return offsetTimezone;
      }

      console.warn('IBD | Countdown | Could not detect timezone using any method');
      return null;
    } catch (error) {
      console.warn('IBD | Countdown | Error detecting timezone:', error);
      return null;
    }
  }

  /**
   * Gets the current time in the specified timezone or detected timezone.
   * @returns {Date} Current time in the appropriate timezone
   */
  #getCurrentTimeInTimezone() {
    const now = new Date();
    const timezoneToUse = this.#detectedTimezone || this.#timezone;

    // If using detected timezone or specific timezone, convert to that timezone
    if (timezoneToUse && timezoneToUse !== 'auto') {
      try {
        // Convert to the specified timezone
        const timeInTimezone = new Date(now.toLocaleString('en-US', { timeZone: timezoneToUse }));

        // Get the timezone offset to adjust for the conversion
        const utcTime = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
        const timezoneOffset = timeInTimezone.getTime() - utcTime.getTime();

        return new Date(now.getTime() + timezoneOffset);
      } catch (error) {
        console.warn('IBD | Countdown | Invalid timezone, falling back to system timezone:', timezoneToUse);
        return now; // Fallback to system timezone
      }
    }

    // Fallback to system timezone
    return now;
  }

  /**
   * Calculates the time remaining until the next Monday at 24:00 in the specified timezone.
   * @returns {number} Time remaining in milliseconds
   */
  #getTimeRemaining() {
    const now = this.#getCurrentTimeInTimezone();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Map weekday names to numbers
    const weekdayMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
    const targetDay = weekdayMap[/** @type {keyof typeof weekdayMap} */ (this.#endWeekday)] ?? 1; // Default to Monday

    // Parse end time (format: "HH:MM" or "24:00")
    const [endHour, endMinute] = this.#endTime.split(':').map(Number);
    const targetHour = (endHour === 24 ? 0 : endHour) ?? 0;
    const targetMinute = endMinute ?? 0;

    // Calculate days until target weekday
    let daysUntilTarget = (targetDay - currentDay + 7) % 7;
    if (daysUntilTarget === 0) {
      // If it's the target day, check if we've passed the end time
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentSecond = now.getSeconds();

      // If it's past the end time, target next occurrence
      if (
        currentHour > targetHour ||
        (currentHour === targetHour && currentMinute > targetMinute) ||
        (currentHour === targetHour && currentMinute === targetMinute && currentSecond >= 0)
      ) {
        daysUntilTarget = 7; // Next occurrence
      }
    }

    // Create target date for next occurrence at end time in the specified timezone
    const targetDate = new Date(now);
    targetDate.setDate(now.getDate() + daysUntilTarget + (endHour === 24 ? 1 : 0));
    targetDate.setHours(targetHour, targetMinute, 0, 0);

    return Math.max(0, targetDate.getTime() - now.getTime());
  }

  /**
   * Formats a number with leading zeros.
   * @param {number} num - The number to format
   * @param {number} digits - Number of digits (default: 2)
   * @returns {string} Formatted number string
   */
  #formatNumber(num, digits = 2) {
    return num.toString().padStart(digits, '0');
  }

  /**
   * Updates the display with new time values.
   * @param {number} days - Days remaining
   * @param {number} hours - Hours remaining
   * @param {number} minutes - Minutes remaining
   * @param {number} seconds - Seconds remaining
   */
  #updateDisplay(days, hours, minutes, seconds) {
    this.#updateDigitGroup(this.#daysElements, this.#formatNumber(days, 2));
    this.#updateDigitGroup(this.#hoursElements, this.#formatNumber(hours, 2));
    this.#updateDigitGroup(this.#minutesElements, this.#formatNumber(minutes, 2));
    this.#updateDigitGroup(this.#secondsElements, this.#formatNumber(seconds, 2));

    // Update accessibility announcements (only for significant changes)
    this.#updateAccessibilityAnnouncement(days, hours, minutes, seconds);
  }

  /**
   * Updates accessibility announcements for screen readers.
   * Only announces significant changes to avoid overwhelming users.
   * @param {number} days - Days remaining
   * @param {number} hours - Hours remaining
   * @param {number} minutes - Minutes remaining
   * @param {number} seconds - Seconds remaining
   */
  #updateAccessibilityAnnouncement(days, hours, minutes, seconds) {
    if (!this.#announcementElement) return;

    const totalHours = days * 24 + hours;

    // Only announce when:
    // 1. First load
    // 2. Hours change
    // 3. Days change
    // 4. Final 15 minutes
    const shouldAnnounce =
      this.#lastAnnouncedHours === -1 || // First load
      this.#lastAnnouncedHours !== totalHours || // Hours changed
      (totalHours === 0 && minutes <= 15); // Final 15 minutes

    if (shouldAnnounce) {
      let announcement = '';

      if (totalHours === 0 && minutes <= 0) {
        announcement = 'Countdown has ended';
      } else if (totalHours === 0 && minutes <= 15) {
        announcement = `Less than 15 minutes remaining`;
      } else if (totalHours === 0) {
        announcement = `${minutes} minutes remaining`;
      } else if (totalHours < 24) {
        const remainingMinutes = minutes;
        if (remainingMinutes === 0) {
          announcement = `${totalHours} hours remaining`;
        } else {
          announcement = `${totalHours} hours and ${remainingMinutes} minutes remaining`;
        }
      } else {
        const remainingDays = Math.floor(totalHours / 24);
        const remainingHours = totalHours % 24;
        if (remainingHours === 0) {
          announcement = `${remainingDays} days remaining`;
        } else {
          announcement = `${remainingDays} days and ${remainingHours} hours remaining`;
        }
      }

      this.#announcementElement.textContent = announcement;
      this.#lastAnnouncedHours = totalHours;
    }
  }

  /**
   * Updates a group of digit elements with a new value.
   * @param {HTMLElement[]} elements - Array of digit elements
   * @param {string} newValue - The new value to display (e.g., "15")
   */
  #updateDigitGroup(elements, newValue) {
    if (elements.length === 0) return;

    // Split the value into individual digits
    const digits = newValue.split('');

    elements.forEach((element, index) => {
      const digitIndex = elements.length - digits.length + index;
      const digitValue = digits[digitIndex] || '0';
      this.#updateFlipElement(element, digitValue);
    });
  }

  /**
   * Updates a flip element with new value, creating flip animation.
   * @param {HTMLElement} element - The flip element to update
   * @param {string} newValue - The new value to display
   */
  #updateFlipElement(element, newValue) {
    const currentValue = element.dataset.currentValue || '00';

    if (currentValue === newValue) return;

    // Add flip animation class
    element.classList.add('ibd-countdown__digit--flipping');

    // Update the value after a short delay to allow flip animation
    setTimeout(() => {
      element.dataset.currentValue = newValue;
      element.textContent = newValue;
      element.classList.remove('ibd-countdown__digit--flipping');
    }, 150);
  }

  /**
   * Starts the countdown timer.
   */
  #startCountdown() {
    this.#countdownInterval = setInterval(() => {
      const timeRemaining = this.#getTimeRemaining();

      if (timeRemaining <= 0) {
        this.#resetCountdown();
        return;
      }

      const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

      this.#updateDisplay(days, hours, minutes, seconds);
    }, 1000);
  }

  /**
   * Stops the countdown timer.
   */
  #stopCountdown() {
    if (this.#countdownInterval) {
      clearInterval(this.#countdownInterval);
      this.#countdownInterval = null;
    }
  }

  /**
   * Resets the countdown and restarts it.
   */
  #resetCountdown() {
    this.#stopCountdown();
    // Small delay to ensure smooth reset
    setTimeout(() => {
      this.#startCountdown();
    }, 100);
  }

  async connectedCallback() {
    super.connectedCallback();

    // Get configuration from data attributes
    this.#endWeekday = this.dataset.endWeekday ?? this.#endWeekday;
    this.#endTime = this.dataset.endTime ?? this.#endTime;
    this.#timezone = this.dataset.timezone ?? this.#timezone;

    // Find display elements - now looking for individual digit elements
    this.#daysElements = Array.from(this.querySelectorAll('[data-countdown-days] [data-digit]'));
    this.#hoursElements = Array.from(this.querySelectorAll('[data-countdown-hours] [data-digit]'));
    this.#minutesElements = Array.from(this.querySelectorAll('[data-countdown-minutes] [data-digit]'));
    this.#secondsElements = Array.from(this.querySelectorAll('[data-countdown-seconds] [data-digit]'));

    // Find accessibility announcement element
    this.#announcementElement = this.querySelector('#countdown-announcement');

    if (
      this.#daysElements.length === 0 ||
      this.#hoursElements.length === 0 ||
      this.#minutesElements.length === 0 ||
      this.#secondsElements.length === 0
    ) {
      console.error('IBD | Countdown | Missing required countdown digit elements');
      return;
    }

    // Detect timezone if set to 'auto'
    if (this.#timezone === 'auto') {
      this.#detectedTimezone = await this.#detectUserTimezone();
    }

    // Initialize display with current time
    const timeRemaining = this.#getTimeRemaining();
    const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

    this.#updateDisplay(days, hours, minutes, seconds);

    // Start the countdown
    this.#startCountdown();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopCountdown();
  }
}

if (!customElements.get('ibd-countdown')) {
  customElements.define('ibd-countdown', IbdCountdown);
}
